import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

// ⚠️ ADMINS ONLY, AND THAT IS THE POINT OF IT. Everything this table carries is
// plumbing - kb_sync, kb_coverage, kb_audit, email_ses - and none of it is anything
// an accounts or ops person can do a thing about. Mercedes, 2026-09-01, reporting
// "a new code that's being thrown when opening Approvals": it was this banner saying
// "kb_audit: 7 doc(s) missing name/description frontmatter", sitting in red above her
// invoices. It had nothing to do with Approvals and nothing to do with her. A warning
// shown to somebody who cannot act on it is not a warning, it is noise that makes the
// real ones invisible - so it now shows to the people who fix these things.
//
// Polls system_health every 60s. Renders nothing when all components are
// healthy. Shows a sticky banner at the top of the app when anything is
// in 'warning' or 'failing'. Click to expand for details + a per-row
// dismiss (dismissal lasts the session — the underlying issue still
// shows up after a tab refresh).

// ⚠️ DOCUMENTATION HYGIENE IS NOT A SYSTEM HEALTH PROBLEM. Greg, 2026-09-02:
// "there is an alert right there about a kb audit, and that is really for George. I
// do not even need that." `kb_audit` and `kb_coverage` report knowledge-base files
// missing frontmatter or coverage - a repo chore, with no outage behind it and
// nothing to do from inside CPx. Sitting in a red bar above everybody's work, they
// were teaching people to ignore the bar, which is how a real failure gets missed.
// ⚠️ `kb_sync` STAYS. That one means the knowledge base has stopped updating, which
// is a job that is actually broken.
const NOT_WORTH_A_BANNER = new Set(['kb_audit', 'kb_coverage']);

const STATUS_STYLES = {
  failing: { bg: '#fef2f2', border: '#fca5a5', icon: '#dc2626', text: '#7f1d1d' },
  warning: { bg: '#fffbeb', border: '#fcd34d', icon: '#d97706', text: '#78350f' },
};

function formatRelative(ts) {
  if (!ts) return '';
  const ms = Date.now() - new Date(ts).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export default function SystemHealthBanner() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set()); // session-scoped

  // ⚠️ Read once per render rather than held in state: a role list that arrives
  // after login should never leave a stale banner on somebody's screen.
  // The roles field is an ARRAY on the session and a comma string in Neon, and code
  // here has crashed on that difference before - both shapes are handled.
  const user = getCurrentUser();
  const roles = Array.isArray(user?.roles)
    ? user.roles
    : String(user?.roles || '').split(',');
  const isAdmin = roles.map((r) => String(r).trim().toLowerCase()).includes('admin');

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const rows = await neonQuery('system_health_active', {});
      const all = Array.isArray(rows) ? rows : (rows?.rows || []);
      const list = all.filter(i => !NOT_WORTH_A_BANNER.has(i.component));
      setItems(list);
    } catch { /* ignore — banner stays hidden */ }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load, isAdmin]);

  const visible = isAdmin ? items.filter(i => !dismissed.has(i.component)) : [];
  if (visible.length === 0) return null;

  const worst = visible.some(i => i.status === 'failing') ? 'failing' : 'warning';
  const s = STATUS_STYLES[worst];
  const headline = visible.length === 1
    ? `${visible[0].component}: ${visible[0].message || visible[0].status}`
    : `${visible.length} system${visible.length === 1 ? '' : 's'} reporting issues`;

  return (
    <div style={{
      background: s.bg, borderBottom: `1px solid ${s.border}`, color: s.text,
      fontSize: 12.5, padding: '8px 14px', display: 'flex', alignItems: 'flex-start',
      gap: 10, flexShrink: 0, fontFamily: 'inherit',
    }}>
      <AlertTriangle size={16} style={{ color: s.icon, flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <button
          onClick={() => setExpanded(v => !v)}
          style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontWeight: 600, textAlign: 'left', fontFamily: 'inherit' }}
        >
          {headline} <span style={{ opacity: 0.7, fontWeight: 400 }}>{expanded ? '▴' : '▾'}</span>
        </button>
        {expanded && (
          <ul style={{ margin: '8px 0 4px 0', padding: '0 0 0 18px', listStyle: 'disc' }}>
            {visible.map(i => (
              <li key={i.component} style={{ marginBottom: 4 }}>
                <strong>{i.component}</strong> — <em style={{ textTransform: 'uppercase', fontSize: 10.5 }}>{i.status}</em> · {i.message || '(no message)'} · last check {formatRelative(i.last_check)}
                <button
                  onClick={() => setDismissed(prev => { const n = new Set(prev); n.add(i.component); return n; })}
                  style={{ marginLeft: 8, background: 'none', border: 'none', color: s.icon, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}
                >dismiss</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
